import {
  useEffect,
  useRef,
  useState,
} from "react";

import "./LocationPicker.css";

export type EventLocation = {
  location: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
};

type LocationPickerProps = {
  value: EventLocation;
  onChange: (
    value: EventLocation,
  ) => void;
};

type PlaceSuggestion = {
  placeId: string;
  mainText: string;
  secondaryText: string;
};

let mapsLoader: Promise<void> | null = null;

function googleMaps() {
  return (
    window as Window & {
      google?: any;
    }
  ).google;
}

function loadGoogleMaps() {
  if (googleMaps()?.maps?.places) {
    return Promise.resolve();
  }

  if (mapsLoader) {
    return mapsLoader;
  }

  const key = import.meta.env
    .VITE_GOOGLE_MAPS_API_KEY;

  if (!key) {
    return Promise.reject(
      new Error(
        "Google Maps is not configured yet.",
      ),
    );
  }

  mapsLoader = new Promise(
    (resolve, reject) => {
      const existing =
        document.querySelector(
          'script[data-saradium-maps="true"]',
        );

      if (existing) {
        existing.addEventListener(
          "load",
          () => resolve(),
          { once: true },
        );

        existing.addEventListener(
          "error",
          () =>
            reject(
              new Error(
                "Google Maps could not load.",
              ),
            ),
          { once: true },
        );

        return;
      }

      const script =
        document.createElement("script");

      script.dataset.saradiumMaps = "true";
      script.async = true;
      script.defer = true;
      script.src =
        "https://maps.googleapis.com/maps/api/js" +
        `?key=${encodeURIComponent(key)}` +
        "&libraries=places";

      script.onload = () => resolve();

      script.onerror = () =>
        reject(
          new Error(
            "Google Maps could not load.",
          ),
        );

      document.head.appendChild(script);
    },
  );

  return mapsLoader;
}

function readableLocation(
  place: any,
) {
  const name = String(place.name || "").trim();

  const address = String(
    place.formatted_address || "",
  ).trim();

  if (!name) {
    return address;
  }

  if (
    !address ||
    address.toLowerCase().startsWith(
      name.toLowerCase(),
    )
  ) {
    return name;
  }

  return `${name}, ${address}`;
}

export function LocationPicker({
  value,
  onChange,
}: LocationPickerProps) {
  const [query, setQuery] =
    useState(value.location);

  const [suggestions, setSuggestions] =
    useState<PlaceSuggestion[]>([]);

  const [searching, setSearching] =
    useState(false);

  const [locating, setLocating] =
    useState(false);

  const [error, setError] = useState("");

  const serviceRef = useRef<any>(null);

  useEffect(() => {
    setQuery(value.location);
  }, [value.location]);

  useEffect(() => {
    const cleanQuery = query.trim();

    if (cleanQuery.length < 3) {
      setSuggestions([]);
      setSearching(false);
      return;
    }

    const timer = window.setTimeout(() => {
      void searchPlaces(cleanQuery);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [query]);

  async function placeService() {
    await loadGoogleMaps();

    const maps = googleMaps();

    if (!serviceRef.current) {
      serviceRef.current =
        new maps.maps.places.AutocompleteService();
    }

    return serviceRef.current;
  }

  async function searchPlaces(
    text: string,
  ) {
    setSearching(true);
    setError("");

    try {
      const service =
        await placeService();

      service.getPlacePredictions(
        {
          input: text,
          componentRestrictions: {
            country: "my",
          },
          types: ["establishment", "geocode"],
        },
        (
          predictions: any[] | null,
          status: string,
        ) => {
          const maps = googleMaps();

          if (
            status !==
              maps.maps.places
                .PlacesServiceStatus.OK ||
            !predictions
          ) {
            setSuggestions([]);
            setSearching(false);
            return;
          }

          setSuggestions(
            predictions.slice(0, 5).map(
              (place) => ({
                placeId: place.place_id,
                mainText:
                  place
                    .structured_formatting
                    ?.main_text ||
                  place.description,
                secondaryText:
                  place
                    .structured_formatting
                    ?.secondary_text ||
                  "",
              }),
            ),
          );

          setSearching(false);
        },
      );
    } catch (caught) {
      setSuggestions([]);
      setSearching(false);

      setError(
        caught instanceof Error
          ? caught.message
          : "Place search is unavailable.",
      );
    }
  }

  async function selectPlace(
    suggestion: PlaceSuggestion,
  ) {
    setSearching(true);
    setError("");
    setSuggestions([]);

    try {
      await loadGoogleMaps();

      const maps = googleMaps();

      const service =
        new maps.maps.places.PlacesService(
          document.createElement("div"),
        );

      service.getDetails(
        {
          placeId: suggestion.placeId,
          fields: [
            "place_id",
            "name",
            "formatted_address",
            "geometry",
          ],
        },
        (
          place: any,
          status: string,
        ) => {
          setSearching(false);

          if (
            status !==
              maps.maps.places
                .PlacesServiceStatus.OK ||
            !place?.geometry?.location
          ) {
            setError(
              "That place could not be selected. Try another result.",
            );
            return;
          }

          const location =
            readableLocation(place);

          setQuery(location);

          onChange({
            location,
            latitude:
              place.geometry.location.lat(),
            longitude:
              place.geometry.location.lng(),
            placeId:
              String(place.place_id || ""),
          });
        },
      );
    } catch (caught) {
      setSearching(false);

      setError(
        caught instanceof Error
          ? caught.message
          : "Place selection is unavailable.",
      );
    }
  }

  async function useCurrentLocation() {
    if (!navigator.geolocation) {
      setError(
        "This browser does not support current location.",
      );
      return;
    }

    setLocating(true);
    setError("");
    setSuggestions([]);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude =
          position.coords.latitude;

        const longitude =
          position.coords.longitude;

        try {
          await loadGoogleMaps();

          const maps = googleMaps();

          const geocoder =
            new maps.maps.Geocoder();

          geocoder.geocode(
            {
              location: {
                lat: latitude,
                lng: longitude,
              },
            },
            (
              results: any[] | null,
              status: string,
            ) => {
              setLocating(false);

              const first = results?.[0];

              const location =
                status === "OK" &&
                first?.formatted_address
                  ? first.formatted_address
                  : `Current location (${latitude.toFixed(
                      5,
                    )}, ${longitude.toFixed(5)})`;

              setQuery(location);

              onChange({
                location,
                latitude,
                longitude,
                placeId:
                  String(
                    first?.place_id || "",
                  ),
              });
            },
          );
        } catch {
          setLocating(false);

          const location =
            `Current location (${latitude.toFixed(
              5,
            )}, ${longitude.toFixed(5)})`;

          setQuery(location);

          onChange({
            location,
            latitude,
            longitude,
          });
        }
      },
      (positionError) => {
        setLocating(false);

        setError(
          positionError.code ===
          positionError.PERMISSION_DENIED
            ? "Location permission was not allowed."
            : "Your current location could not be found.",
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 12_000,
        maximumAge: 60_000,
      },
    );
  }

  function typeLocation(
    nextLocation: string,
  ) {
    setQuery(nextLocation);
    setSuggestions([]);

    onChange({
      location: nextLocation,
    });
  }

  return (
    <div className="location-picker">
      <div className="location-picker__topline">
        <span>EVENT LOCATION</span>

        <button
          type="button"
          className="location-picker__current"
          onClick={() =>
            void useCurrentLocation()
          }
          disabled={locating}
        >
          {locating
            ? "Finding location…"
            : "⌖ Use my location"}
        </button>
      </div>

      <div className="location-picker__search">
        <span aria-hidden="true">⌕</span>

        <input
          value={query}
          maxLength={1000}
          placeholder="Search a place or enter an address"
          onChange={(event) =>
            typeLocation(
              event.target.value,
            )
          }
        />

        {searching && (
          <small>Searching…</small>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="location-picker__results">
          {suggestions.map((place) => (
            <button
              type="button"
              key={place.placeId}
              onClick={() =>
                void selectPlace(place)
              }
            >
              <span>⌖</span>

              <div>
                <strong>
                  {place.mainText}
                </strong>

                {place.secondaryText && (
                  <small>
                    {place.secondaryText}
                  </small>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {value.latitude !== undefined &&
        value.longitude !== undefined && (
          <a
            className="location-picker__selected"
            href={
              "https://www.google.com/maps/search/" +
              `?api=1&query=${value.latitude},` +
              value.longitude
            }
            target="_blank"
            rel="noreferrer"
          >
            <span>●</span>

            <div>
              <strong>
                Exact location selected
              </strong>

              <small>
                Open the selected pin in Maps
              </small>
            </div>

            <b>↗</b>
          </a>
        )}

      {error && (
        <p className="location-picker__error">
          {error}
        </p>
      )}
    </div>
  );
}